export type ApiBaseUrlOptions = {
  fallback?: string;
  requireInProd?: boolean;
  errorMessage?: string;
};

const DEFAULT_LOCAL_FALLBACK_URL = "http://localhost:8080";

const normalizeEnvUrl = (value?: string) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export const getApiBaseUrl = (options: ApiBaseUrlOptions = {}) => {
  const { fallback = DEFAULT_LOCAL_FALLBACK_URL, requireInProd = true, errorMessage } = options;
  // Bundlers (Bun's --env / serve.static env, Vite define) can only inline the
  // static process.env form; the keyed readPublicEnv lookup resolves only in
  // prod builds that define the whole import.meta.env object.
  const url =
    normalizeEnvUrl(process.env.BUN_PUBLIC_API_BASE_URL) ??
    normalizeEnvUrl(readPublicEnv("BUN_PUBLIC_API_BASE_URL"));
  const isProd = readPublicEnv("NODE_ENV") === "production";

  if (!url && isProd && requireInProd) {
    throw new Error(
      errorMessage ?? "[API_BASE_URL] BUN_PUBLIC_API_BASE_URL is missing in production build.",
    );
  }

  return url ?? fallback;
};

type ImportMetaWithEnv = ImportMeta & {
  readonly env?: Record<string, string | undefined>;
};

type GlobalWithProcessEnv = typeof globalThis & {
  readonly process?: {
    readonly env?: Record<string, string | undefined>;
  };
};

const readPublicEnv = (key: string): string | undefined => {
  const importMetaEnv = (import.meta as ImportMetaWithEnv).env;
  if (importMetaEnv?.[key]) {
    return importMetaEnv[key];
  }

  return (globalThis as GlobalWithProcessEnv).process?.env?.[key];
};
